import React, { useRef, useState, useEffect } from "react";
import { motion, useInView, useAnimation } from "framer-motion";
import PropTypes from "prop-types";

/**
 * AnimatedCard - A universal card component with customizable entrance animations
 * 
 * @param {Object} props - Component props
 * @param {string} props.direction - Animation direction: 'left', 'right', 'top', 'bottom'
 * @param {number} props.delay - Animation delay in seconds
 * @param {number} props.duration - Animation duration in seconds
 * @param {string} props.className - Additional CSS classes for the card
 * @param {boolean} props.once - Whether animation should only play once
 * @param {React.ReactNode} props.children - Card content
 * @param {number} props.distance - How far to animate from (in pixels)
 * @param {Object} props.style - Additional inline styles
 * @param {boolean} props.resetOnExit - Whether to reset animation when element exits viewport
 * @returns {React.ReactElement} Animated card component
 */
const AnimatedCard = ({
  direction = "left",
  delay = 0,
  duration = 0.6,
  className = "",
  once = false,
  children,
  distance = 50,
  style = {},
  resetOnExit = true,
}) => {
  const cardRef = useRef(null);
  const controls = useAnimation();
  const isInView = useInView(cardRef, { amount: 0.2 });
  const [hasAnimated, setHasAnimated] = useState(false);

  // Define initial positions based on direction
  const getInitialPosition = () => {
    switch (direction) {
      case "right":
        return { x: distance, opacity: 0 };
      case "left":
        return { x: -distance, opacity: 0 };
      case "bottom":
        return { y: distance, opacity: 0 };
      case "top":
        return { y: -distance, opacity: 0 };
      case "fadeIn":
        return { opacity: 0 };
      default:
        return { x: -distance, opacity: 0 };
    }
  };

  // Define animation variants
  const variants = {
    hidden: getInitialPosition(),
    visible: {
      x: 0,
      y: 0,
      opacity: 1,
      transition: {
        duration,
        delay,
        ease: "easeOut",
      },
    },
  };

  useEffect(() => {
    // Skip reset if we've set "once" to true and the element has already animated
    if (once && hasAnimated) {
      return;
    }

    if (isInView) {
      controls.start("visible");
      setHasAnimated(true);
    } else if (resetOnExit) {
      // Only reset if resetOnExit is true
      controls.start("hidden");
    }
  }, [isInView, controls, once, hasAnimated, resetOnExit]);

  return (
    <motion.div
      ref={cardRef}
      className={`animated-card ${className}`}
      initial="hidden"
      animate={controls}
      variants={variants}
      style={style}
    >
      {children}
    </motion.div>
  );
};

AnimatedCard.propTypes = {
  direction: PropTypes.oneOf(["left", "right", "top", "bottom", "fadeIn"]),
  delay: PropTypes.number,
  duration: PropTypes.number,
  className: PropTypes.string,
  once: PropTypes.bool,
  children: PropTypes.node.isRequired,
  distance: PropTypes.number,
  style: PropTypes.object,
  resetOnExit: PropTypes.bool,
};

export default AnimatedCard; 